import { Card, CardContent, CardHeader, Chip, Skeleton, Stack, Typography } from "@mui/material";
import * as Sentry from "@sentry/react";
import dayjs from "dayjs";
import LocalizedFormat from "dayjs/plugin/localizedFormat";
import React from "react";
import { FormattedMessage, useIntl } from "react-intl";

import AssessmentsOverview from "../../components/AssessmentsOverview";
import Layout from "../../components/Layout";
import MessagePage from "../../components/MessagePage";
import { campaignIsCompleted } from "../../globals/types";
import { useCampaign } from "../../hooks/useFormsLists";
import { isNotFound } from "../../lib/formsApi";

dayjs.extend(LocalizedFormat);

const ID_PREFIX = "campaign-page-";

const CampaignPage: React.FC = () => {
    const { data, isPending: loading, error } = useCampaign();
    const { formatDate, formatMessage } = useIntl();

    if (isNotFound(error) || data?.assessments.length === 0) {
        return <MessagePage reason="404" />;
    }

    if (error || !data) {
        if (loading) {
            return (
                <Layout appBar>
                    <Stack direction="row" justifyContent="center" alignContent="center">
                        <Stack width="80vw">
                            <Card sx={{ minHeight: "70vh" }}>
                                <CardHeader sx={{ minHeight: "14rem", backgroundColor: "#ffebd2", padding: "2rem" }} />
                                <CardContent sx={{ minHeight: "20rem" }}>
                                    <Skeleton height="1+?%" width="100%" />
                                </CardContent>
                            </Card>
                        </Stack>
                    </Stack>
                </Layout>
            );
        }
        if (error) Sentry.captureException(error);
        if (!data) Sentry.captureException("no campaign data");
        return <MessagePage reason="other" />;
    }

    const assessments = data.assessments;
    const campaignName = `${formatMessage({ defaultMessage: "Campaign Name:" })} ${data.name}`;
    const campaignCompleted = campaignIsCompleted(assessments);

    const welcomeMessage = `${formatMessage({ defaultMessage: "Welcome" })}${
        assessments[0].type === "external" ? `, ${assessments[0].supplierName}` : "!"
    }`;

    return (
        <Layout appBar>
            <Stack direction="row" justifyContent="center" alignContent="center" paddingTop={10}>
                <Stack width="80vw">
                    <Card sx={{ minHeight: "70vh" }}>
                        <CardHeader
                            sx={{ height: "10vh", minHeight: "10rem", backgroundColor: "#ffebd2", padding: 2 }}
                            subheader={
                                <Stack alignItems="center" spacing={3}>
                                    <Stack alignItems="center" spacing={1}>
                                        <Typography color="#1D212B" variant="h6">
                                            {welcomeMessage}
                                        </Typography>
                                    </Stack>
                                    {data.dueDate && (
                                        <Stack alignItems="center">
                                            <Typography data-testid={`${ID_PREFIX}dueDate`} variant="caption" noWrap>
                                                <FormattedMessage
                                                    defaultMessage="Due date: {date}"
                                                    values={{ date: formatDate(data.dueDate) }}
                                                />
                                            </Typography>
                                        </Stack>
                                    )}
                                </Stack>
                            }
                        />
                        <CardContent>
                            <Stack padding={2} spacing={2}>
                                <Stack direction="row" alignItems="center" justifyContent="space-between">
                                    <Typography>{campaignName}</Typography>
                                    {assessments.length === 1 && (
                                        <Chip
                                            variant="status"
                                            color={campaignCompleted ? "success" : "neutral"}
                                            label={
                                                campaignCompleted
                                                    ? formatMessage({ defaultMessage: "Completed" })
                                                    : formatMessage({ defaultMessage: "Not Submitted" })
                                            }
                                        />
                                    )}
                                </Stack>
                                <Stack spacing={1}>
                                    <Stack direction="row" justifyContent="space-between" alignContent="center">
                                        <Typography variant="subtitle2">
                                            <FormattedMessage defaultMessage="Description" />
                                        </Typography>
                                    </Stack>
                                    <Stack padding={1}>
                                        <Typography
                                            sx={{ whiteSpace: "pre-line" }}
                                            variant="body2"
                                            data-testid={`${ID_PREFIX}description`}
                                        >
                                            {data.description || ""}
                                        </Typography>
                                    </Stack>
                                </Stack>
                                <Stack paddingTop={3}>
                                    <Stack direction="row" overflow="auto">
                                        <AssessmentsOverview assessments={assessments} />
                                    </Stack>
                                </Stack>
                            </Stack>
                        </CardContent>
                    </Card>
                </Stack>
            </Stack>
        </Layout>
    );
};

export default CampaignPage;
